import {Avatar, Choice, Separator, showNotification} from 'platform/components';
import {Box, Heading, HStack, Show, Space, Text, VStack} from 'platform/foundation';

import {useRef} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';

import {head, isNil} from 'ramda';

import {useLogoutMutation} from '@dms/api/core';
import {selectCurrentUserInfo, useSetUserAwayMutation} from '@dms/api/customer';
import {selectUserBranchOptions} from '@dms/api/features';
import {browserStorageKey} from '@dms/config';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  getWorkspaceFromUri,
  myProfileRoutes,
  redirectToWorkspace,
  settingsRoutes,
  sourcingRoutes,
} from '@dms/routes';
import {
  EMPTY_PLACEHOLDER,
  handleApiError,
  useCurrentUserAvatarUrl,
  usePermissions,
  useUserFullName,
} from '@dms/shared';
import {isSourcingStorageKeyKeptOnLogout} from '@dms/sourcing';

import {pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {omneticReleaseNotes} from '../../../releaseNotes';
import {useAvailabilityStatus} from '../hooks/useAvailabilityStatus';
import {useIsMobileNavigation} from '../hooks/useIsMobileNavigation';
import {useSwitchActiveBranch} from '../hooks/useSwitchActiveBranch';
import {DelegatedAbsences} from './DelegatedAbsences';
import {LatestReleaseLink} from './LatestReleaseLink';
import {ShowOnMobileNavigation} from './ShowOnMobileNavigation';
import {TopBarItem, TopBarItemRef} from './TopBarItem';
import {TopBarMenuItem} from './TopBarMenuItem';
import {UserAvatarAuthProfileChoice} from './UserAvatarAuthProfileChoice';
import {UserAvatarButton} from './UserAvatarButton';

const DEFAULT_APP_VERSION = '1.9.0';
const LATEST_APP_VERSION = head(omneticReleaseNotes)?.versionNumber ?? DEFAULT_APP_VERSION;
const BRANCH_MENU_MAX_HEIGHT_STEPS = 33;

interface UserAvatarProps extends RequiredTestIdProps {
  areTopBarSelectsRelocated: boolean;
}

export function UserAvatar(props: UserAvatarProps) {
  const topBarItemRef = useRef<TopBarItemRef>(null);
  const isMobileNavigation = useIsMobileNavigation();
  const location = useLocation();

  const [hasReadSettingsPermission] = usePermissions({permissionKeys: ['readSettings']});
  const [logoutUser] = useLogoutMutation();
  const {data: userInfo} = useSelector(selectCurrentUserInfo);
  const userAvatarUrl = useCurrentUserAvatarUrl();

  const userFullName = useUserFullName();
  const branchOptions = useSelector(selectUserBranchOptions);
  const {switchActiveBranch, isChangingBranch, activeBranchId} = useSwitchActiveBranch();
  const [setUserAway, {isLoading: isAwayLoading}] = useSetUserAwayMutation();
  const {status} = useAvailabilityStatus();

  const handleClosePopup = () => {
    if (isMobileNavigation) {
      topBarItemRef.current?.close();
    }
  };

  const handleBranchChange = (value: string | null) => {
    if (isNil(value)) {
      return;
    }

    switchActiveBranch(value, {onSuccess: handleClosePopup});
  };

  const handleToggleAway = () => {
    if (isNil(userInfo)) {
      return;
    }

    const newIsAway = !(userInfo.isAway ?? false);

    setUserAway({userId: userInfo.id, isAway: newIsAway})
      .unwrap()
      .then(() => {
        showNotification.success(
          newIsAway ? i18n.t('user.awayStatus.toastAwayOn') : i18n.t('user.awayStatus.toastAwayOff')
        );
      })
      .catch(handleApiError);
  };

  const handleLogout = () => {
    const accessToken = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);
    const {workspace} = getWorkspaceFromUri();

    logoutUser({accessToken, workspace})
      .unwrap()
      .then(() => {
        pushGtmEvent({
          event: 'button_click',
          event_id: 'user_logout',
        });
        const keptSourcingEntries = Object.keys(localStorage)
          .filter(isSourcingStorageKeyKeptOnLogout)
          // The key was just listed, so getItem never returns null here.
          .map((key) => [key, localStorage.getItem(key) ?? ''] as const);
        localStorage.clear();
        sessionStorage.clear();
        localStorage.setItem(browserStorageKey.LAST_PROMO_FEATURE_SEEN, i18n.t('ad.feature.id'));
        localStorage.setItem(browserStorageKey.LAST_KNOWN_LANGUAGE, i18n.language);
        keptSourcingEntries.forEach(([key, value]) => localStorage.setItem(key, value));
        redirectToWorkspace({currentUserEmail: userInfo?.email});
      })
      .catch(handleApiError);
  };

  const activeBranch = branchOptions?.find((branch) => branch.value === activeBranchId) ?? null;
  const hasMultipleBranches = (branchOptions?.length ?? 0) > 1;
  const shouldShowBranchChoice =
    hasMultipleBranches && (props.areTopBarSelectsRelocated || isMobileNavigation);

  if (isNil(userInfo)) {
    return null;
  }

  return (
    <TopBarItem
      gtmEventId="top_bar_user_avatar_open"
      ref={topBarItemRef}
      isFullScreen={isMobileNavigation}
      trigger={(isOpen) => (
        <UserAvatarButton
          isOpen={isOpen}
          status={status}
          userAvatarUrl={userAvatarUrl}
          data-testid={suffixTestId('myProfileAndSettings-button', props)}
        />
      )}
    >
      <Box backgroundColor="general.white" padding={4}>
        <Box width={['auto', 'auto', 75]}>
          <HStack align="center">
            <Avatar
              data-testid={suffixTestId('myProfileAndSettings', props)}
              name={userFullName}
              size="default"
              imageUrl={userAvatarUrl}
              variant="circle"
            />

            <Space horizontal={4} />
            <VStack width="100%">
              <Heading
                alternative
                headingLevel={2}
                size={4}
                data-testid={suffixTestId('myProfileAndSettings-name', props)}
              >
                {userFullName ?? EMPTY_PLACEHOLDER}
              </Heading>
              <Text
                size="small"
                color="secondary"
                overflowWrap="anywhere"
                data-testid={suffixTestId('myProfileAndSettings-email', props)}
              >
                {userInfo.email}
              </Text>
              <Text
                size="xSmall"
                color="secondary"
                overflowWrap="anywhere"
                data-testid={suffixTestId('myProfileAndSettings-version', props)}
              >
                {i18n.t('general.labels.appVersion') + `: ${LATEST_APP_VERSION}`}
              </Text>
            </VStack>
          </HStack>

          <ShowOnMobileNavigation>
            <Separator spacing={4} />
            <Show when={!hasMultipleBranches}>
              <Heading
                data-testid={suffixTestId('myProfileAndSettings-activeBranch', props)}
                headingLevel={2}
                size={4}
              >
                {activeBranch?.label ?? EMPTY_PLACEHOLDER}
              </Heading>
              <Space vertical={4} />
            </Show>
            <TopBarMenuItem
              as="button"
              onClick={handleToggleAway}
              icon={userInfo.isAway ? 'action/check_circle' : 'alert/warning'}
              label={
                userInfo.isAway
                  ? i18n.t('user.awayStatus.goAvailable')
                  : i18n.t('user.awayStatus.goAway')
              }
              isLoading={isAwayLoading}
              isDisabled={isAwayLoading}
              data-testid="layout-header-away-toggle"
            />
          </ShowOnMobileNavigation>

          <Show whenFeatureEnabled={featureFlags.USER_ABSENCE}>
            <DelegatedAbsences data-testid={suffixTestId('myProfileAndSettings', props)} />
          </Show>
        </Box>

        <Separator spacing={4} />
        <VStack spacing={1}>
          <TopBarMenuItem
            to={myProfileRoutes.personalInformation}
            onClick={handleClosePopup}
            icon="social/person"
            label={i18n.t('entity.user.labels.myProfile')}
          />
          <ShowOnMobileNavigation when={hasReadSettingsPermission}>
            <TopBarMenuItem
              to={settingsRoutes.userManagement}
              onClick={handleClosePopup}
              icon="action/settings"
              label={i18n.t('page.settings.labels.title')}
            />
          </ShowOnMobileNavigation>
          <ShowOnMobileNavigation>
            <LatestReleaseLink />
          </ShowOnMobileNavigation>
          <Show when={shouldShowBranchChoice}>
            <Choice<string>
              label={i18n.t('entity.branch.labels.selectedBranch')}
              data-testid={suffixTestId('myProfileAndSettings-activeBranch', props)}
              name="activeBranch"
              value={activeBranchId ?? null}
              options={branchOptions ?? []}
              onChange={handleBranchChange}
              closeMenuOnScroll={false}
              isNotClearable
              isLoading={isChangingBranch}
              isDisabled={isChangingBranch}
              maxMenuHeight={BRANCH_MENU_MAX_HEIGHT_STEPS}
            />
          </Show>
          <Show
            whenFeatureEnabled={featureFlags.AUTH_PROFILE_SELECTOR}
            when={
              (props.areTopBarSelectsRelocated || isMobileNavigation) &&
              !location.pathname.includes(sourcingRoutes.home)
            }
          >
            <UserAvatarAuthProfileChoice />
          </Show>
        </VStack>
        <Separator spacing={4} />
        <TopBarMenuItem
          as="button"
          onClick={handleLogout}
          label={i18n.t('general.actions.logout')}
          data-testid={suffixTestId('myProfileAndSettings-logout', props)}
          icon="action/exit_to_app"
        />
      </Box>
    </TopBarItem>
  );
}
